/**
 * 이상한 방 - 소켓 전송 (서버 너머의 호스트와 말하는 `Transport` · 재연결 백오프 · `hello{token}` 자동 재전송)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것 - 시계는 서버의 것)
 *            로드맵 M6(`wsTransport.ts` - 재연결 백오프 · `hello{token}` 자동 재전송 · 연결 전 `act`는 버리고 `rejected`)
 *            `net/transport.ts` 머리말(소켓 전송의 `tick`은 아무것도 하지 않는다)
 *
 * 세션(`game/matchSession.ts`)은 이 파일과 루프백(`loopbackTransport.ts`)을 구분하지 않는다 - 같은 `Transport` 표면이고,
 * 다른 것은 **연결 상태**(`onStatus`) 하나다. 그것도 세션이 아니라 앱(`app/lobbyFlow.ts`)이 읽어 배너(`ui/connection.ts`)에
 * 넘긴다. 세션에 "지금 소켓인가"를 묻는 분기가 생기면 봇 판이 서버 판의 검증 수단이라는 전제가 깨진다(브리프).
 *
 * ## `hello`는 여기서 보낸다 - 열릴 때마다
 *
 * 첫 메시지는 `hello`여야 하고(서버 `gateway.ts`), 끊겼다 다시 열린 소켓도 서버에게는 낯선 소켓이라 다시 `hello`다. 그때
 * `token`이 있어야 같은 좌석으로 돌아간다(규칙 2 - 유예 안이면 탄환·발견 보존). 토큰은 `welcome`에 실려 오므로 이 파일이
 * 지나가는 메시지에서 **읽어 둔다** - 세션이 토큰을 알 이유가 없고, 앱은 `onToken`으로 받아 저장만 한다(`app/netBoot.ts`).
 * 방을 만든 경우 `hello.roomCode`는 빈 문자열이었지만 재접속 `hello`는 `welcome.roomCode`(서버가 발급한 코드)로 간다.
 *
 * ## 연결 전 `act`는 버리고 `rejected`를 만든다
 *
 * 소켓이 열리기 전(또는 끊긴 동안)의 사격을 큐에 쌓아 두었다가 보내면 그 발은 몇 초 뒤의 판에 도착한다 - 리듀서는 그때의
 * 페이즈로 판정하고, 사람은 방금 누른 것이 "왜 지금 터졌는지" 모른다. 그래서 큐가 없다. 대신 `rejected{cseq, reason:'unknown'}`을
 * **이 자리에서 만들어** 배달한다 - 세션은 거절을 받으면 기다리던 탭을 지우므로(`matchSessionInbox.ts`) 회색 고리가 영원히
 * 남지 않는다. `act`가 아닌 메시지(`lobby` · `resync` · `ping`)는 조용히 버린다 - 되돌려 줄 번호가 없다.
 *
 * ## 재연결은 지수 백오프, 서버가 끊은 것은 다시 잇지 않는다
 *
 * 닫힘에는 두 종류가 있다. 네트워크가 죽은 것(와이파이 · 폰 잠김)은 다시 잇는다 - 0.5초부터 두 배씩, 상한 8초. 지터는 없다
 * (`Math.random` 금지 - 한 방에 사람이 여덟이라 동시 재접속 폭주가 문제 되는 규모가 아니다). 서버가 `error{code}`를 보내고
 * 끊은 것(`version` · `no-room` · `full` · `in-progress` · `superseded`)은 다시 이어도 같은 답이므로 **다시 잇지 않는다** - 특히
 * `superseded`(다른 창이 같은 좌석을 가져갔다)에서 다시 이으면 두 창이 좌석을 서로 빼앗으며 영원히 번갈아 끊긴다.
 *
 * ## `tick`은 아무것도 하지 않는다
 *
 * 시계는 서버의 것이다(11.3절). 설정 패널이 열려 프레임이 멈춰도 서버의 판은 흐르고, 자막이 걷힌 뒤 첫 `clock`이 그 차이를
 * 당긴다(`game/matchClock.ts`). 루프백과 다른 단 하나의 자리이고, 그 차이는 `loopbackTransport.ts` 머리말에 있다.
 *
 * 소켓 생성자와 타이머는 주입할 수 있다(`socketFactory` · `schedule`) - 테스트가 포트 없이 재연결 순서를 검사하기 위해서다.
 * 기본값은 브라우저의 `WebSocket`과 `setTimeout`이고, 그 둘을 아는 자리는 아래 두 함수뿐이다.
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`). 서버는 이 파일을 import 하지 않는다.
 */

import type { PlayerId } from '../core/types'
import {
  decodeServer,
  encode,
  PROTOCOL_VERSION,
  type ClientMessage,
  type ErrorCode,
  type RoomVisibility,
  type ServerMessage,
} from './protocol'
import type { MessageListener, Transport } from './transport'

/* --- 상수 ------------------------------------------------------------------------ */

/** 첫 재연결까지의 간격(ms). 끊긴 직후는 대개 순간 단절이라 짧게 시작한다. */
export const RECONNECT_BASE_MS = 500
/** 재연결 간격의 상한(ms). 이보다 길면 "돌아왔다"가 유예(60초) 안에 몇 번 시도되지 않는다. */
export const RECONNECT_MAX_MS = 8_000

/* --- 타입 ------------------------------------------------------------------------ */

export type WsStatus = 'connecting' | 'open' | 'reconnecting' | 'closed'

export type WsStatusInfo = Readonly<{
  /** 지금까지의 재연결 시도 횟수. 첫 연결은 0이다. */
  attempt: number
  /** 서버가 끊으며 말한 이유. 없으면 `null`(네트워크 단절 · 우리가 닫음). */
  error: ErrorCode | null
}>

export type WsStatusListener = (status: WsStatus, info: WsStatusInfo) => void

/**
 * 이 전송이 소켓에게 요구하는 전부. 브라우저 `WebSocket`을 `browserSocket`이 이 모양으로 감싸고, 테스트는 가짜를 넣는다.
 * 이벤트 핸들러는 속성이다 - 생성 직후 이 파일이 넷을 전부 채우므로 리스너 목록이 필요 없다.
 */
export type SocketLike = {
  send: (text: string) => void
  close: () => void
  onopen: (() => void) | null
  onmessage: ((event: Readonly<{ data: unknown }>) => void) | null
  onclose: (() => void) | null
  onerror: (() => void) | null
}

export type SocketFactory = (url: string) => SocketLike

/** 타이머 주입. 돌려주는 함수가 취소다. 기본은 `setTimeout`/`clearTimeout`. */
export type Schedule = (fn: () => void, delayMs: number) => () => void

export type HelloRequest = Readonly<{
  /** 빈 문자열(`CREATE_ROOM_CODE`)이면 방을 만든다. */
  roomCode: string
  name: string
  /** 있으면 재접속이다. 서버가 모르는 토큰이면 새 사람으로 앉힌다(`gateway.ts`). */
  token?: string | undefined
  /** 방을 **만들 때만** 뜻이 있다(M14). 없으면 서버가 비공개로 연다 - 그것이 지금 동작이다. */
  visibility?: RoomVisibility | undefined
}>

export type WsTransportOptions = Readonly<{
  url: string
  hello: HelloRequest
  /** `welcome`이 토큰을 줄 때마다. 앱이 저장한다(`app/netBoot.ts`) - 이 파일은 저장소를 모른다. */
  onToken?: ((token: string, roomCode: string, playerId: PlayerId) => void) | undefined
  socketFactory?: SocketFactory | undefined
  schedule?: Schedule | undefined
}>

export type WsTransport = Transport &
  Readonly<{
    onStatus: (listener: WsStatusListener) => () => void
    status: () => WsStatus
  }>

/* --- 기본 소켓 · 타이머 ---------------------------------------------------------------- */

/** 브라우저 `WebSocket`을 `SocketLike`로. 이 파일에서 `WebSocket`을 아는 자리는 여기 하나다. */
function browserSocket(url: string): SocketLike {
  const ws = new WebSocket(url)
  const like: SocketLike = {
    send: (text) => ws.send(text),
    close: () => ws.close(),
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
  }
  ws.onopen = () => like.onopen?.()
  ws.onmessage = (event) => like.onmessage?.({ data: event.data })
  ws.onclose = () => like.onclose?.()
  ws.onerror = () => like.onerror?.()
  return like
}

const defaultSchedule: Schedule = (fn, delayMs) => {
  const id = setTimeout(fn, delayMs)
  return () => clearTimeout(id)
}

/** n번째 재연결까지의 간격. 1 → 0.5초, 2 → 1초, … 상한 8초. 순수 함수라 테스트가 표로 고정한다. */
export function reconnectDelayMs(attempt: number): number {
  const n = Math.max(1, Math.floor(attempt))
  return Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (n - 1))
}

/* --- 전송 ------------------------------------------------------------------------ */

export function createWsTransport(options: WsTransportOptions): WsTransport {
  const socketFactory = options.socketFactory ?? browserSocket
  const schedule = options.schedule ?? defaultSchedule

  const listeners: MessageListener[] = []
  const statusListeners: WsStatusListener[] = []

  let socket: SocketLike | null = null
  let isOpen = false
  let closed = false
  let status: WsStatus = 'connecting'
  let attempt = 0
  let lastError: ErrorCode | null = null
  let cancelTimer: (() => void) | null = null

  /** 재접속 `hello`의 재료. 방을 만든 뒤에는 서버가 발급한 코드로 바뀐다(머리말). */
  let roomCode = options.hello.roomCode
  let token: string | undefined = options.hello.token

  function setStatus(next: WsStatus): void {
    status = next
    const info: WsStatusInfo = { attempt, error: lastError }
    for (const listener of [...statusListeners]) listener(next, info)
  }

  function deliver(msg: ServerMessage): void {
    for (const listener of [...listeners]) listener(msg)
  }

  function sendHello(): void {
    const hello: ClientMessage = {
      type: 'hello',
      v: PROTOCOL_VERSION,
      roomCode,
      name: options.hello.name,
      ...(token === undefined ? {} : { token }),
      /*
       * 방을 **만드는 첫 `hello`에만** 싣는다(M14). 재접속의 `roomCode`는 서버가 발급한 코드라 만드는 것이 아니고,
       * 그때도 실으면 "이미 있는 방의 공개 여부를 hello가 바꾼다"는 두 번째 경로가 생긴다 - 바꾸는 길은
       * `lobby{setVisibility}` 하나여야 방장 검사도 한 곳이다.
       */
      ...(options.hello.visibility === undefined || roomCode !== options.hello.roomCode
        ? {}
        : { visibility: options.hello.visibility }),
    }
    socket?.send(encode(hello))
  }

  function onText(text: string): void {
    const decoded = decodeServer(text)
    if (!decoded.ok) {
      // 서버가 만든 메시지가 코덱을 못 지나는 것은 버전 드리프트다. 버리되 남긴다 - 조용히 삼기면 "가끔 스텝이 안 온다"가 된다.
      console.warn('[wsTransport] 서버 메시지를 읽지 못했다', decoded.reason)
      return
    }
    const msg = decoded.message
    if (msg.type === 'welcome') {
      token = msg.token
      roomCode = msg.roomCode
      options.onToken?.(msg.token, msg.roomCode, msg.playerId)
    } else if (msg.type === 'error') {
      // 서버가 끊을 것이다. 이 뒤의 close는 재연결 대상이 아니다(머리말).
      lastError = msg.code
    }
    deliver(msg)
  }

  function open(): void {
    const ws = socketFactory(options.url)
    socket = ws
    isOpen = false
    ws.onopen = () => {
      if (closed || socket !== ws) return
      isOpen = true
      /*
       * 백오프를 되감는다. 안 되감으면 서로 무관한 두 번째·세 번째 단절의 대기가 계속 두 배씩 늘어 곧 상한(8초)에
       * 눌러앉고, 머리말의 "끊긴 직후는 대개 순간 단절이라 짧게 시작한다"가 첫 단절에만 참이 된다. 이동 중 연결이
       * 정확히 그 모양이다. `attempt`는 리스너에게도 나가는 값이라(`WsStatusInfo.attempt`) 단조 증가하면 거짓말이다.
       */
      attempt = 0
      sendHello()
      setStatus('open')
    }
    ws.onmessage = (event) => {
      if (closed || socket !== ws || typeof event.data !== 'string') return
      onText(event.data)
    }
    // 오류는 곧 close로 이어진다. 여기서 따로 하는 일은 없다 - 두 번 세면 재연결이 두 번 잡힌다.
    ws.onerror = () => {}
    ws.onclose = () => {
      // 갈아 끼운 옛 소켓의 늦은 close. 새 소켓의 상태를 건드리면 안 된다.
      if (socket !== ws) return
      socket = null
      isOpen = false
      if (closed) return
      if (lastError !== null) {
        setStatus('closed')
        return
      }
      attempt += 1
      setStatus('reconnecting')
      cancelTimer = schedule(() => {
        cancelTimer = null
        if (!closed) open()
      }, reconnectDelayMs(attempt))
    }
  }

  open()

  return {
    send(msg: ClientMessage): void {
      if (closed) return
      if (isOpen && socket !== null) {
        socket.send(encode(msg))
        return
      }
      // 연결 전·끊긴 동안의 요청은 버린다(머리말). `act`만 거절을 되돌려 준다 - 기다리는 탭을 지워야 한다.
      if (msg.type !== 'act') return
      const rejected: ServerMessage = { type: 'rejected', cseq: msg.cseq, reason: 'unknown' }
      queueMicrotask(() => {
        if (!closed) deliver(rejected)
      })
    },

    onMessage(listener: MessageListener): () => void {
      listeners.push(listener)
      return () => {
        const at = listeners.indexOf(listener)
        if (at >= 0) listeners.splice(at, 1)
      }
    },

    onStatus(listener: WsStatusListener): () => void {
      statusListeners.push(listener)
      return () => {
        const at = statusListeners.indexOf(listener)
        if (at >= 0) statusListeners.splice(at, 1)
      }
    },

    status: () => status,

    // 시계는 서버의 것이다(머리말). 프레임 델타로 밀 것이 없다.
    tick(): void {},

    close(): void {
      if (closed) return
      closed = true
      cancelTimer?.()
      cancelTimer = null
      const ws = socket
      socket = null
      isOpen = false
      ws?.close()
      listeners.length = 0
      setStatus('closed')
      statusListeners.length = 0
    },
  }
}
